/**
 * BCP-47 locale-tag resolution for Intl/Luxon locale-aware formatting.
 *
 * Mirrors the calendar widget's #1530 convention (CalendarGrid.tsx
 * `toBcp47`/`isEnglish`, used there for month/weekday labels) so a future
 * consolidation can drop these exports in without a behavior change — this
 * PR does not refactor the calendar to use them (#1429 scope: booking only).
 *
 * `locale` is the operator's configured/detected value in `xx_YY` shape
 * (`useLocaleDetection`'s `detectedLocale`).
 */

/** BCP-47 tag for a `xx_YY` locale string, or `null` if it isn't well-formed. */
export function toBcp47(locale: string | undefined): string | null {
  if (!locale) return null
  const tag = locale.replace('_', '-')
  return /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(tag) ? tag : null
}

/** True for an English locale (`en`, `en_US`, `en_GB`, …) or an unset one. */
export function isEnglish(locale: string | undefined): boolean {
  return !locale || /^en([_-]|$)/i.test(locale)
}

/**
 * The BCP-47 tag to pass to `Intl`/Luxon, or `null` when the caller should
 * keep its pre-locale-aware default (English, unset, or an unusable tag) —
 * callers treat `null` as "use today's hard-coded behavior" so the en_US /
 * unset path stays byte-identical to before locale-aware formatting existed.
 */
export function resolveLocaleTag(locale: string | undefined): string | null {
  return isEnglish(locale) ? null : toBcp47(locale)
}
